import {keepPreviousData, useQueries, useQuery, useQueryClient} from '@github-ui/react-query'
import {getHighlightedEvent} from '@github-ui/timeline-items/HighlightedEvent'
import {useCallback, useMemo, useState} from 'react'

import {
  fetchTimelineItems,
  type TimelineItemsConnection,
  type TimelineItemsPageParams,
  timelineItemsQueryKey,
} from './fetch-timeline-items'
import type {IssueTimelineItemFragment} from './IssueTimelineItem'
import {
  assembleTimelineItems,
  countItemsRemaining,
  dedupeItems,
  isHighlightReady,
  subtractItems,
  type TimelineCounters,
  type TimelineSlot,
} from './timeline-assembly'
import {asTimelineItem} from './timeline-item-parity'
import {isEventHighlighted} from './use-timeline-highlight'
import type {UseTimelineItemsCoreResult} from './use-timeline-items-core'

export type UseTimelineItemsTanstackProps = {
  owner: string
  repo: string
  number: number
  highlightedEvent?: string
  page?: number
  /** Matches the Relay path's first page size so both produce the same initial view. */
  pageSize?: number
}

type LoadMoreOptions = {onComplete?: () => void}

type HighlightPage = {direction: 'before' | 'after'; connection: TimelineItemsConnection}

const DEFAULT_PAGE_SIZE = 15
const FOCUS_NEIGHBOR_COUNT = 3

function edgeNodes(connection: TimelineItemsConnection | undefined): IssueTimelineItemFragment[] {
  if (!connection) return []

  return (connection.edges ?? []).flatMap(edge => (edge?.node ? [asTimelineItem(edge.node)] : []))
}

function timelineQueryOptions(owner: string, repo: string, number: number, params: TimelineItemsPageParams) {
  return {
    queryKey: timelineItemsQueryKey(owner, repo, number, params),
    queryFn: ({signal}: {signal?: AbortSignal}) => fetchTimelineItems({owner, repo, number, ...params}, signal),
    throwOnError: true as const,
  }
}

type TimelineQueryOptions = ReturnType<typeof timelineQueryOptions>

/**
 * Read-only TanStack implementation of the timeline, selected by
 * `issue_viewer_paved_path`. Returns the same shape as the Relay hook, so the flag flips at
 * a single call site and the rendering path never learns which source it read from.
 *
 * Like the Relay path it reads two windows -- oldest and newest -- with a "load more" gap between
 * them, plus a third around a deep-linked event. Writes are not reflected here yet.
 */
export function useTimelineItemsTanstack({
  owner,
  repo,
  number,
  highlightedEvent,
  page,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseTimelineItemsTanstackProps): UseTimelineItemsCoreResult {
  const queryClient = useQueryClient()
  const highlight = useMemo(() => getHighlightedEvent(highlightedEvent), [highlightedEvent])

  const [frontExtraPages, setFrontExtraPages] = useState<TimelineItemsConnection[]>([])
  const [backExtraPages, setBackExtraPages] = useState<TimelineItemsConnection[]>([])
  const [highlightPagesByFocus, setHighlightPagesByFocus] = useState<Record<string, HighlightPage[]>>({})

  const queryOptions = useCallback(
    (params: TimelineItemsPageParams) => timelineQueryOptions(owner, repo, number, params),
    [owner, repo, number],
  )

  const frontQuery = useQuery({...queryOptions({first: pageSize}), placeholderData: keepPreviousData})

  // Oldest first, so each page fetched with `after` extends the end.
  const frontPages = useMemo(
    () => (frontQuery.data ? [frontQuery.data, ...frontExtraPages] : []),
    [frontQuery.data, frontExtraPages],
  )

  const frontItems = useMemo(() => dedupeItems(frontPages.flatMap(edgeNodes)), [frontPages])
  const totalItemCount = frontQuery.data?.totalCount ?? 0

  // `page` renders one explicit page with no gap, so it has no newest-items window.
  const backEnabled = !page && totalItemCount > pageSize

  const backQuery = useQuery({
    ...queryOptions({last: pageSize}),
    enabled: backEnabled,
    placeholderData: keepPreviousData,
  })

  // Each page fetched with `before` is older than the ones already held, so extras come first.
  const backPages = useMemo(
    () => (backQuery.data ? [...[...backExtraPages].reverse(), backQuery.data] : []),
    [backQuery.data, backExtraPages],
  )

  const backItems = useMemo(
    () => subtractItems(dedupeItems(backPages.flatMap(edgeNodes)), frontItems),
    [backPages, frontItems],
  )

  const loadedItems = useMemo(() => [...frontItems, ...backItems], [frontItems, backItems])

  const highlightIsPreloaded = useMemo(
    () => (highlight ? loadedItems.some(event => isEventHighlighted(event, highlight)) : false),
    [loadedItems, highlight],
  )

  const focusText = highlight ? `${highlight.prefix}-${highlight.id}` : undefined

  const highlightParams: TimelineItemsPageParams | undefined = focusText
    ? {focusText, first: 1, focusNeighborCount: FOCUS_NEIGHBOR_COUNT}
    : undefined

  // Soft-navigating between deep links re-renders this hook without unmounting it, so pages have to
  // be held per focus: a flat list would wrap the new focus in the old one's pages and let a load
  // still in flight land in the wrong window. Returning to a focus reuses what it already fetched.
  const highlightPages = useMemo(
    () => (focusText ? (highlightPagesByFocus[focusText] ?? []) : []),
    [highlightPagesByFocus, focusText],
  )

  // An empty `useQueries` list registers nothing, so an issue opened without a deep link doesn't
  // leave a disabled query keyed on empty params behind.
  const highlightQueries: TimelineQueryOptions[] =
    highlightParams && !highlightIsPreloaded ? [queryOptions(highlightParams)] : []

  const [highlightQuery] = useQueries({queries: highlightQueries})

  const highlightData = highlightQuery?.data

  const highlightItems = useMemo(() => {
    if (!highlightData) return []

    // Before-pages arrive progressively older, so they render in reverse of the order fetched.
    const before = [...highlightPages.filter(p => p.direction === 'before')]
      .reverse()
      .flatMap(p => edgeNodes(p.connection))
    const after = highlightPages.filter(p => p.direction === 'after').flatMap(p => edgeNodes(p.connection))

    return dedupeItems([...before, ...edgeNodes(highlightData), ...after])
  }, [highlightData, highlightPages])

  // Each item paged in ahead of the focus moves the window's start that much earlier.
  const prependedHighlightCount = useMemo(
    () =>
      highlightPages.filter(p => p.direction === 'before').reduce((sum, p) => sum + edgeNodes(p.connection).length, 0),
    [highlightPages],
  )

  const highlightStartPosition = Math.max((highlightData?.beforeFocusCount ?? 0) - prependedHighlightCount, 0)

  const uniqueHighlightItems = useMemo(
    () => (highlightItems.length > 0 ? subtractItems(highlightItems, loadedItems) : []),
    [highlightItems, loadedItems],
  )

  const hasLazyLoadedHighlights = uniqueHighlightItems.length > 0

  const timelineCounters: TimelineCounters = useMemo(
    () => ({
      frontItems: frontItems.length,
      backItems: backItems.length,
      highlightItems: uniqueHighlightItems.length,
      highlightStartPosition,
    }),
    [frontItems.length, backItems.length, uniqueHighlightItems.length, highlightStartPosition],
  )

  const {front: itemsRemainingFront, back: itemsRemainingBack} = countItemsRemaining({
    hasLazyLoadedHighlights,
    timelineCounters,
    totalItemCount,
    frontItemCount: frontItems.length,
    backItemCount: backItems.length,
  })

  // One request per activation; the endpoint clamps the page size it is given.
  const loadPages = useCallback(
    (
      count: number,
      held: TimelineItemsConnection[],
      paramsFor: (from: TimelineItemsConnection, size: number) => TimelineItemsPageParams | null,
      apply: (pages: TimelineItemsConnection[]) => void,
      options?: LoadMoreOptions,
    ) => {
      void (async () => {
        const from = held.at(-1)
        const params = from && paramsFor(from, count)
        // `LoadMore` clears its own loading state only in `onComplete`, so every path has to reach
        // it -- including this one, or the control stays disabled with nothing in flight.
        if (!params) return options?.onComplete?.()

        try {
          const connection = await queryClient.fetchQuery(queryOptions(params))
          if ((connection.edges ?? []).length > 0) apply([connection])
        } catch {
          // Keep the marker in place so the read can be retried.
        }

        options?.onComplete?.()
      })()
    },
    [queryClient, queryOptions],
  )

  const loadMoreFrontItems = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      loadPages(
        count,
        frontPages,
        (from, size) =>
          from.pageInfo?.hasNextPage && from.pageInfo.endCursor ? {first: size, after: from.pageInfo.endCursor} : null,
        pages => setFrontExtraPages(current => [...current, ...pages]),
        options,
      )
    },
    [loadPages, frontPages],
  )

  const loadMoreBackItems = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      loadPages(
        count,
        [...backPages].reverse(),
        (from, size) =>
          from.pageInfo?.hasPreviousPage && from.pageInfo.startCursor
            ? {last: size, before: from.pageInfo.startCursor}
            : null,
        pages => setBackExtraPages(current => [...current, ...pages]),
        options,
      )
    },
    [loadPages, backPages],
  )

  // Both sides continue from the last page already held, not from the focus window's original
  // cursor -- re-asking from the focus would refetch the same page on every activation.
  const highlightSidePages = useCallback(
    (direction: 'before' | 'after') =>
      highlightPages.filter(entry => entry.direction === direction).map(entry => entry.connection),
    [highlightPages],
  )

  const appendHighlightPages = useCallback(
    (direction: 'before' | 'after') => (connections: TimelineItemsConnection[]) => {
      if (!focusText) return
      setHighlightPagesByFocus(byFocus => ({
        ...byFocus,
        [focusText]: [...(byFocus[focusText] ?? []), ...connections.map(connection => ({direction, connection}))],
      }))
    },
    [focusText],
  )

  const loadBeforeHighlight = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      if (!highlightData) return options?.onComplete?.()

      loadPages(
        count,
        [highlightData, ...highlightSidePages('before')],
        (from, size) =>
          from.pageInfo?.hasPreviousPage && from.pageInfo.startCursor
            ? {last: size, before: from.pageInfo.startCursor}
            : null,
        appendHighlightPages('before'),
        options,
      )
    },
    [loadPages, highlightData, highlightSidePages, appendHighlightPages],
  )

  const loadAfterHighlight = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      if (!highlightData) return options?.onComplete?.()

      loadPages(
        count,
        [highlightData, ...highlightSidePages('after')],
        (from, size) =>
          from.pageInfo?.hasNextPage && from.pageInfo.endCursor ? {first: size, after: from.pageInfo.endCursor} : null,
        appendHighlightPages('after'),
        options,
      )
    },
    [loadPages, highlightData, highlightSidePages, appendHighlightPages],
  )

  const slots: TimelineSlot[] | null = useMemo(
    () =>
      assembleTimelineItems({
        frontItems,
        backItems,
        emptyBackItems: [],
        uniqueLoadedHighlightItems: uniqueHighlightItems,
        totalItemCount,
        itemsRemainingFront,
        itemsRemainingBack,
        hasLazyLoadedHighlights,
        loadingInitialBackItems: backEnabled && backQuery.isLoading,
        page,
      }),
    [
      frontItems,
      backItems,
      uniqueHighlightItems,
      totalItemCount,
      itemsRemainingFront,
      itemsRemainingBack,
      hasLazyLoadedHighlights,
      backEnabled,
      backQuery.isLoading,
      page,
    ],
  )

  const timelineItems = useMemo(
    () =>
      slots?.map(slot => {
        if (slot.type !== 'load-marker') return slot

        const {type: _marker, ...rest} = slot
        return slot.position === 'top'
          ? {
              ...rest,
              type: 'load' as const,
              loadFromTop: loadMoreFrontItems,
              loadFromBottom: hasLazyLoadedHighlights ? loadBeforeHighlight : loadMoreBackItems,
            }
          : {...rest, type: 'load' as const, loadFromTop: loadAfterHighlight, loadFromBottom: loadMoreBackItems}
      }) ?? null,
    [slots, hasLazyLoadedHighlights, loadMoreFrontItems, loadMoreBackItems, loadBeforeHighlight, loadAfterHighlight],
  )

  return {
    timelineItems,
    totalItemCount,
    highlightedReady: isHighlightReady(totalItemCount, backItems.length, timelineCounters, highlightIsPreloaded),
  }
}
